import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, FileText, Save, Eye, Check } from 'lucide-react';
import {
  fetchEmployeeProfile,
  fetchFormSubmission,
  fetchFormStatus,
  saveDraft,
  submitForm,
  adminSaveDraft,
  adminSubmitForm,
  adminFetchFormSubmission,
  adminFetchEmployeeProfile
} from '../../../services/employeeFormsApi';

const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const textareaClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none';

export default function PFNominationForm() {
  const navigate = useNavigate();
  const { formCode, employeeId } = useParams();
  const [formData, setFormData] = useState({
    depositorName: '',
    depositorAddress: '',
    nominees: [
      {
        name: '',
        address: '',
        relationship: '',
        age: '',
        sharePercentage: ''
      }
    ],
    declarationAccepted: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load employee profile and existing draft on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const code = formCode || 'PF_NOM';
        let submissionData = null;
        let profileData = null;

        if (employeeId) {
            try {
                const submission = await adminFetchFormSubmission(employeeId, code);
                if (submission && submission.form_data) {
                    submissionData = submission.form_data;
                }
            } catch (e) {
                console.log('No admin draft found');
            }

            try {
                profileData = await adminFetchEmployeeProfile(employeeId);
            } catch (e) {
                console.log('Failed to fetch profile in admin mode');
            }
        } else {
            try {
              const status = await fetchFormStatus(code);
              if (status.status === 'SUBMITTED' || status.status === 'APPROVED') {
                navigate(`/employee/forms/${code}/preview`);
                return;
              }
            } catch (error) {
              console.log('No form status found');
            }

            try {
              const submission = await fetchFormSubmission(code);
              if (submission && submission.form_data) {
                submissionData = submission.form_data;
              }
            } catch (error) {
              console.log('No existing draft found');
            }

            try {
              profileData = await fetchEmployeeProfile();
            } catch (error) {
              console.log('Failed to fetch profile');
            }
        }

        setFormData(prev => {
            let newData = { ...prev };
            if (submissionData) {
                newData = { ...newData, ...submissionData };
            }
            if (profileData) {
                newData.depositorName = profileData.full_name || '';
                newData.depositorAddress = profileData.current_address || '';
            }
            return newData;
        });

      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [formCode, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleNomineeChange = (index, field, value) => {
    const updatedNominees = [...formData.nominees];
    updatedNominees[index][field] = value;
    setFormData(prev => ({
      ...prev,
      nominees: updatedNominees
    }));
    // Clear error for this field
    const errorKey = `nominee_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const addNominee = () => {
    setFormData(prev => ({
      ...prev,
      nominees: [
        ...prev.nominees,
        {
          name: '',
          address: '',
          relationship: '',
          age: '',
          sharePercentage: ''
        }
      ]
    }));
  };

  const removeNominee = (index) => {
    if (formData.nominees.length > 1) {
      const updatedNominees = formData.nominees.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        nominees: updatedNominees
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Depositor Details
    if (!formData.depositorName.trim()) {
      newErrors.depositorName = 'Depositor Name is required';
    }
    if (!formData.depositorAddress.trim()) {
      newErrors.depositorAddress = 'Depositor Address is required';
    }

    // Nominee Details
    formData.nominees.forEach((nominee, index) => {
      if (!nominee.name.trim()) {
        newErrors[`nominee_${index}_name`] = 'Nominee Name is required';
      }
      if (!nominee.address.trim()) {
        newErrors[`nominee_${index}_address`] = 'Nominee Address is required';
      }
      if (!nominee.relationship.trim()) {
        newErrors[`nominee_${index}_relationship`] = 'Relationship is required';
      }
      if (!nominee.age) {
        newErrors[`nominee_${index}_age`] = 'Age is required';
      } else if (parseInt(nominee.age) <= 0) {
        newErrors[`nominee_${index}_age`] = 'Age must be greater than 0';
      }
      if (!nominee.sharePercentage) {
        newErrors[`nominee_${index}_sharePercentage`] = 'Share Percentage is required';
      } else {
        const share = parseFloat(nominee.sharePercentage);
        if (share < 1 || share > 100) {
          newErrors[`nominee_${index}_sharePercentage`] = 'Share Percentage must be between 1 and 100';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      const code = formCode || 'PF_NOM';
      if (employeeId) {
          await adminSaveDraft(employeeId, code, formData);
      } else {
          await saveDraft(code, formData);
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (validateForm()) {
      // Auto-save draft before preview
      await handleSaveDraft();
      const code = formCode || 'PF_NOM';
      navigate(`/employee/forms/${code}/preview`, { state: formData });
    }
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      // Check if total share percentage equals 100
      const totalShare = formData.nominees.reduce((sum, nominee) => {
        return sum + (parseFloat(nominee.sharePercentage) || 0);
      }, 0);

      if (Math.abs(totalShare - 100) > 0.01) {
        setErrors(prev => ({
          ...prev,
          totalShare: `Total share percentage must equal 100%. Current total: ${totalShare}%`
        }));
        return;
      }

      if (!formData.declarationAccepted) {
        setErrors(prev => ({
          ...prev,
          declaration: 'Please accept the declaration to submit'
        }));
        return;
      }

      try {
        const code = formCode || 'PF_NOM';
        if (employeeId) {
            await adminSubmitForm(employeeId, code, formData);
            navigate(`/employees/${employeeId}/profile?tab=documents`, { replace: true });
        } else {
            await submitForm(code, formData);
            navigate(`/user-dashboard?tab=my-forms`, { replace: true });
        }
      } catch (error) {
          alert('Failed to submit form: ' + error.message);
      }
    }
  };

  const renderNomineeRow = (nominee, index) => (
    <tr key={index} className="border-b border-gray-200">
      <td className="px-3 py-2">
        <input
          type="text"
          value={nominee.name}
          onChange={(e) => handleNomineeChange(index, 'name', e.target.value)}
          placeholder="Nominee Name"
          className={`${inputClass} ${errors[`nominee_${index}_name`] ? 'border-red-300 bg-red-50' : ''}`}
        />
        {errors[`nominee_${index}_name`] && (
          <p className="text-red-600 text-xs mt-1">{errors[`nominee_${index}_name`]}</p>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={nominee.address}
          onChange={(e) => handleNomineeChange(index, 'address', e.target.value)}
          placeholder="Nominee Address"
          className={`${inputClass} ${errors[`nominee_${index}_address`] ? 'border-red-300 bg-red-50' : ''}`}
        />
        {errors[`nominee_${index}_address`] && (
          <p className="text-red-600 text-xs mt-1">{errors[`nominee_${index}_address`]}</p>
        )}
      </td>
      <td className="px-3 py-2">
        <select
          value={nominee.relationship}
          onChange={(e) => handleNomineeChange(index, 'relationship', e.target.value)}
          className={`${inputClass} ${errors[`nominee_${index}_relationship`] ? 'border-red-300 bg-red-50' : ''}`}
        >
          <option value="">Select Relationship</option>
          <option value="Mother">Mother</option>
          <option value="Father">Father</option>
          <option value="Daughter">Daughter</option>
          <option value="Son">Son</option>
          <option value="Widow">Widow</option>
          <option value="Unmarried Daughter">Unmarried Daughter</option>
          <option value="Minor Son up to 25 years">Minor Son up to 25 years</option>
        </select>
        {errors[`nominee_${index}_relationship`] && (
          <p className="text-red-600 text-xs mt-1">{errors[`nominee_${index}_relationship`]}</p>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          type="number"
          value={nominee.age}
          onChange={(e) => handleNomineeChange(index, 'age', e.target.value)}
          placeholder="Age"
          min="1"
          className={`${inputClass} ${errors[`nominee_${index}_age`] ? 'border-red-300 bg-red-50' : ''}`}
        />
        {errors[`nominee_${index}_age`] && (
          <p className="text-red-600 text-xs mt-1">{errors[`nominee_${index}_age`]}</p>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          type="number"
          value={nominee.sharePercentage}
          onChange={(e) => handleNomineeChange(index, 'sharePercentage', e.target.value)}
          placeholder="Share %"
          min="1"
          max="100"
          className={`${inputClass} ${errors[`nominee_${index}_sharePercentage`] ? 'border-red-300 bg-red-50' : ''}`}
        />
        {errors[`nominee_${index}_sharePercentage`] && (
          <p className="text-red-600 text-xs mt-1">{errors[`nominee_${index}_sharePercentage`]}</p>
        )}
      </td>
      <td className="px-3 py-2">
        {formData.nominees.length > 1 && (
          <button
            type="button"
            onClick={() => removeNominee(index)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove Nominee"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading form...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-12 w-12 rounded-lg bg-blue-50 flex items-center justify-center">
            <FileText className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">PF Nomination Form</h1>
            <p className="text-gray-600">Nominate beneficiaries for Provident Fund accumulation.</p>
          </div>
        </div>
      </div>

      {/* Section 1: Depositor Details */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Depositor Details</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Depositor Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.depositorName}
              onChange={(e) => handleInputChange('depositorName', e.target.value)}
              placeholder="Enter depositor name"
              className={`${inputClass} ${errors.depositorName ? 'border-red-300 bg-red-50' : ''} bg-gray-50`}
              readOnly
            />
            {errors.depositorName && (
              <p className="text-red-600 text-sm mt-1">{errors.depositorName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Depositor Address <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.depositorAddress}
              onChange={(e) => handleInputChange('depositorAddress', e.target.value)}
              placeholder="Enter depositor address"
              rows={3}
              className={`${textareaClass} ${errors.depositorAddress ? 'border-red-300 bg-red-50' : ''} bg-gray-50`}
              readOnly
            />
            {errors.depositorAddress && (
              <p className="text-red-600 text-sm mt-1">{errors.depositorAddress}</p>
            )}
          </div>
        </div>
      </div>

      {/* Section 2: Nominee Details */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Nominee Details</h2>
          <button
            type="button"
            onClick={addNominee}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Nominee
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Nominee Name <span className="text-red-500">*</span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Nominee Address <span className="text-red-500">*</span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Relationship <span className="text-red-500">*</span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Age <span className="text-red-500">*</span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Share % <span className="text-red-500">*</span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {formData.nominees.map((nominee, index) => renderNomineeRow(nominee, index))}
            </tbody>
          </table>
        </div>

        {/* Total Share Percentage */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">
            Total Share Percentage: {formData.nominees.reduce((sum, nominee) => sum + (parseFloat(nominee.sharePercentage) || 0), 0)}%
          </span>
          {errors.totalShare && (
            <p className="text-red-600 text-sm mt-1">{errors.totalShare}</p>
          )}
        </div>
      </div>

      {/* Section 3: Declaration */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Declaration</h2>
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            I hereby declare that the above information is true and correct to the best of my knowledge and belief.
            I understand that any false information may result in rejection of my nomination or legal action.
          </p>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.declarationAccepted}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, declarationAccepted: e.target.checked }));
              if (errors.declaration) {
                setErrors(prev => ({ ...prev, declaration: '' }));
              }
            }}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            I hereby declare that the above information is true and correct.
          </span>
        </label>
        {errors.declaration && (
          <p className="text-red-600 text-sm mt-2">{errors.declaration}</p>
        )}
      </div>

      {/* Section 4: Actions */}
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => employeeId ? navigate(`/employees/${employeeId}/profile?tab=documents`) : navigate(-1)}
          className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
        {saveSuccess && (
          <span className="text-green-600 text-sm ml-2">Draft saved successfully!</span>
        )}
        {!employeeId && (
          <button
            type="button"
            onClick={handlePreview}
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
        )}
        {employeeId && (
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              <Check className="w-4 h-4" />
              Save / Submit
            </button>
        )}
      </div>
    </div>
  );
}
